import { Resolver, Query, Mutation, Arg, Ctx, Int, Directive } from 'type-graphql'
import { getRepository, IsNull } from 'typeorm'
import {
  CreateWorkflowRequest,
  ExecuteWorkflowRequest,
  WorkflowExecutionResult,
  LabelingWorkflow as LabelingWorkflowType,
  WorkflowExecution as WorkflowExecutionType,
  LabelingWorkflowList,
  WorkflowExecutionList,
  WorkflowStatus,
  WorkflowStepType,
  TriggerType,
  WorkflowStep
} from '../types/workflow-types.js'
import { LabelingWorkflow as LabelingWorkflowEntity } from './labeling-workflow.js'
import { WorkflowExecution as WorkflowExecutionEntity } from './workflow-execution.js'
import { WorkflowExecutionStep as WorkflowExecutionStepEntity } from './workflow-execution-step.js'
import { DatasetLabelingIntegration } from '@things-factory/integration-label-studio/dist-server/service/dataset-labeling-integration.js'
import { ExternalDataSourceService } from '@things-factory/integration-label-studio/dist-server/service/external-data-source-service.js'
import { LabelStudioAIPredictionService } from '@things-factory/integration-label-studio/dist-server/service/ai-prediction-service.js'

/**
 * Labeling Workflow Service
 *
 * Orchestrates end-to-end labeling workflows with multiple steps
 *
 * Features:
 * - Define multi-step labeling workflows
 * - Auto-execute workflows based on triggers
 * - Track execution progress
 * - Error handling and retry logic
 * - Conditional execution
 * - Database persistence with TypeORM
 */
@Resolver()
export class LabelingWorkflowService {
  // Service dependencies (lazy initialization)
  private datasetIntegration: DatasetLabelingIntegration | null = null
  private externalDataSource: ExternalDataSourceService | null = null
  private aiPredictionService: LabelStudioAIPredictionService | null = null

  private getDatasetIntegration(): DatasetLabelingIntegration {
    if (!this.datasetIntegration) {
      this.datasetIntegration = new DatasetLabelingIntegration()
    }
    return this.datasetIntegration
  }

  private getExternalDataSource(): ExternalDataSourceService {
    if (!this.externalDataSource) {
      this.externalDataSource = new ExternalDataSourceService()
    }
    return this.externalDataSource
  }

  private getAIPredictionService(): LabelStudioAIPredictionService {
    if (!this.aiPredictionService) {
      this.aiPredictionService = new LabelStudioAIPredictionService()
    }
    return this.aiPredictionService
  }

  /**
   * Convert Entity to GraphQL Type
   */
  private toWorkflowType(entity: LabelingWorkflowEntity): LabelingWorkflowType {
    return {
      ...entity,
      steps: JSON.parse(entity.steps)
    } as LabelingWorkflowType
  }

  private toExecutionType(entity: WorkflowExecutionEntity): WorkflowExecutionType {
    return entity as any as WorkflowExecutionType
  }

  /**
   * Create a new labeling workflow
   */
  @Mutation(returns => LabelingWorkflowType, {
    description: 'Create a new labeling workflow'
  })
  @Directive('@privilege(category: "labeling", privilege: "mutation")')
  async createLabelingWorkflow(
    @Arg('input') input: CreateWorkflowRequest,
    @Ctx() context: ResolverContext
  ): Promise<LabelingWorkflowType> {
    console.log(`[Workflow] Creating workflow: ${input.name}`)

    const workflowRepo = getRepository(LabelingWorkflowEntity)

    const workflow = workflowRepo.create({
      domain: context.state.domain,
      creator: context.state.user,
      name: input.name,
      description: input.description,
      projectId: input.projectId,
      triggerType: input.triggerType,
      triggerConfig: input.triggerConfig,
      steps: JSON.stringify(
        input.steps.map((step, index) => ({
          name: step.name,
          type: step.type,
          config: step.config,
          condition: step.condition,
          continueOnError: step.continueOnError !== undefined ? step.continueOnError : true,
          maxRetries: step.maxRetries,
          order: index + 1
        }))
      ),
      status: input.autoStart ? WorkflowStatus.Active : WorkflowStatus.Draft
    })

    await workflowRepo.save(workflow)

    console.log(`[Workflow] Created workflow ${workflow.id} with ${input.steps.length} steps`)

    return this.toWorkflowType(workflow)
  }

  /**
   * Execute a workflow
   */
  @Mutation(returns => WorkflowExecutionResult, {
    description: 'Execute a labeling workflow'
  })
  @Directive('@privilege(category: "labeling", privilege: "mutation")')
  async executeLabelingWorkflow(
    @Arg('input') input: ExecuteWorkflowRequest,
    @Ctx() context: ResolverContext
  ): Promise<WorkflowExecutionResult> {
    const workflowRepo = getRepository(LabelingWorkflowEntity)
    const workflow = await workflowRepo.findOne({
      where: { id: input.workflowId, domain: context.state.domain, deletedAt: IsNull() }
    })

    if (!workflow) {
      throw new Error(`Workflow not found: ${input.workflowId}`)
    }

    console.log(`[Workflow] Executing workflow: ${workflow.name} (${workflow.id})`)

    const steps = JSON.parse(workflow.steps) as WorkflowStep[]

    const executionRepo = getRepository(WorkflowExecutionEntity)
    const execution = executionRepo.create({
      domain: context.state.domain,
      workflow,
      workflowId: workflow.id,
      workflowName: workflow.name,
      status: 'running',
      startedAt: new Date()
    })

    await executionRepo.save(execution)

    // Create execution steps
    const stepRepo = getRepository(WorkflowExecutionStepEntity)
    await Promise.all(
      steps.map((step, index) =>
        stepRepo.save(
          stepRepo.create({
            domain: context.state.domain,
            execution,
            executionId: execution.id,
            stepName: step.name,
            stepType: step.type,
            order: index + 1,
            status: 'pending'
          })
        )
      )
    )

    // Execute asynchronously
    this.executeWorkflowAsync(execution.id, workflow.id, steps, context, input.parameters).catch(error => {
      console.error(`[Workflow] Execution failed:`, error)
    })

    return {
      executionId: execution.id,
      status: 'running',
      summary: `Started execution of ${steps.length} steps`
    }
  }

  /**
   * Get workflow details
   */
  @Query(returns => LabelingWorkflowType, {
    description: 'Get labeling workflow details'
  })
  @Directive('@privilege(category: "labeling", privilege: "query")')
  async labelingWorkflow(
    @Arg('workflowId') workflowId: string,
    @Ctx() context: ResolverContext
  ): Promise<LabelingWorkflowType> {
    const workflowRepo = getRepository(LabelingWorkflowEntity)
    const workflow = await workflowRepo.findOne({
      where: { id: workflowId, domain: context.state.domain, deletedAt: IsNull() }
    })

    if (!workflow) {
      throw new Error(`Workflow not found: ${workflowId}`)
    }

    return this.toWorkflowType(workflow)
  }

  /**
   * List all workflows
   */
  @Query(returns => LabelingWorkflowList, {
    description: 'List all labeling workflows'
  })
  @Directive('@privilege(category: "labeling", privilege: "query")')
  async labelingWorkflows(
    @Arg('projectId', type => Int, { nullable: true }) projectId: number,
    @Ctx() context: ResolverContext
  ): Promise<LabelingWorkflowList> {
    const workflowRepo = getRepository(LabelingWorkflowEntity)
    const queryBuilder = workflowRepo
      .createQueryBuilder('workflow')
      .where('workflow.domain = :domain', { domain: context.state.domain.id })
      .andWhere('workflow.deletedAt IS NULL')

    if (projectId) {
      queryBuilder.andWhere('workflow.projectId = :projectId', { projectId })
    }

    const [entities, total] = await queryBuilder.orderBy('workflow.createdAt', 'DESC').getManyAndCount()
    const items = entities.map(e => this.toWorkflowType(e))

    return { items, total }
  }

  /**
   * Get execution status
   */
  @Query(returns => WorkflowExecutionType, {
    description: 'Get workflow execution status'
  })
  @Directive('@privilege(category: "labeling", privilege: "query")')
  async workflowExecution(
    @Arg('executionId') executionId: string,
    @Ctx() context: ResolverContext
  ): Promise<WorkflowExecutionType> {
    const executionRepo = getRepository(WorkflowExecutionEntity)
    const execution = await executionRepo.findOne({
      where: { id: executionId, domain: context.state.domain, deletedAt: IsNull() },
      relations: ['steps']
    })

    if (!execution) {
      throw new Error(`Execution not found: ${executionId}`)
    }

    return this.toExecutionType(execution)
  }

  /**
   * List executions for a workflow
   */
  @Query(returns => WorkflowExecutionList, {
    description: 'List executions for a workflow'
  })
  @Directive('@privilege(category: "labeling", privilege: "query")')
  async workflowExecutions(
    @Arg('workflowId') workflowId: string,
    @Ctx() context: ResolverContext
  ): Promise<WorkflowExecutionList> {
    const executionRepo = getRepository(WorkflowExecutionEntity)
    const [entities, total] = await executionRepo.findAndCount({
      where: { workflowId, domain: context.state.domain, deletedAt: IsNull() },
      order: { startedAt: 'DESC' }
    })

    const items = entities.map(e => this.toExecutionType(e))
    return { items, total }
  }

  /**
   * Pause a workflow
   */
  @Mutation(returns => LabelingWorkflowType, {
    description: 'Pause a workflow'
  })
  @Directive('@privilege(category: "labeling", privilege: "mutation")')
  async pauseLabelingWorkflow(
    @Arg('workflowId') workflowId: string,
    @Ctx() context: ResolverContext
  ): Promise<LabelingWorkflowType> {
    const workflowRepo = getRepository(LabelingWorkflowEntity)
    const workflow = await workflowRepo.findOne({
      where: { id: workflowId, domain: context.state.domain, deletedAt: IsNull() }
    })

    if (!workflow) {
      throw new Error(`Workflow not found: ${workflowId}`)
    }

    workflow.status = WorkflowStatus.Paused
    await workflowRepo.save(workflow)

    console.log(`[Workflow] Paused workflow: ${workflow.name}`)

    return this.toWorkflowType(workflow)
  }

  /**
   * Resume a workflow
   */
  @Mutation(returns => LabelingWorkflowType, {
    description: 'Resume a paused workflow'
  })
  @Directive('@privilege(category: "labeling", privilege: "mutation")')
  async resumeLabelingWorkflow(
    @Arg('workflowId') workflowId: string,
    @Ctx() context: ResolverContext
  ): Promise<LabelingWorkflowType> {
    const workflowRepo = getRepository(LabelingWorkflowEntity)
    const workflow = await workflowRepo.findOne({
      where: { id: workflowId, domain: context.state.domain, deletedAt: IsNull() }
    })

    if (!workflow) {
      throw new Error(`Workflow not found: ${workflowId}`)
    }

    workflow.status = WorkflowStatus.Active
    await workflowRepo.save(workflow)

    console.log(`[Workflow] Resumed workflow: ${workflow.name}`)

    return this.toWorkflowType(workflow)
  }

  // ============================================================================
  // Private Methods - Workflow Execution Engine
  // ============================================================================

  private async executeWorkflowAsync(
    executionId: string,
    workflowId: string,
    steps: WorkflowStep[],
    context: ResolverContext,
    parametersJson?: string
  ): Promise<void> {
    const startTime = Date.now()
    const parameters = parametersJson ? JSON.parse(parametersJson) : {}

    console.log(`[Workflow] Starting execution ${executionId}`)

    const executionRepo = getRepository(WorkflowExecutionEntity)
    const stepRepo = getRepository(WorkflowExecutionStepEntity)
    const workflowRepo = getRepository(LabelingWorkflowEntity)

    const execution = await executionRepo.findOne({
      where: { id: executionId },
      relations: ['steps', 'workflow']
    })

    if (!execution) {
      throw new Error(`Execution not found: ${executionId}`)
    }

    try {
      for (const [index, workflowStep] of steps.entries()) {
        const executionStep = execution.steps[index]
        executionStep.status = 'running'
        executionStep.startedAt = new Date()
        await stepRepo.save(executionStep)

        console.log(`[Workflow] Executing step ${index + 1}/${steps.length}: ${workflowStep.name}`)

        try {
          // Check condition if specified
          if (workflowStep.condition) {
            const conditionMet = this.evaluateCondition(workflowStep.condition, parameters)
            if (!conditionMet) {
              console.log(`[Workflow] Step ${workflowStep.name} condition not met, skipping`)
              executionStep.status = 'skipped'
              executionStep.completedAt = new Date()
              await stepRepo.save(executionStep)
              continue
            }
          }

          // Execute step
          const stepConfig = JSON.parse(workflowStep.config)
          const stepResult = await this.executeStep(
            workflowStep.type,
            stepConfig,
            execution.workflow.projectId,
            context,
            parameters
          )

          executionStep.status = 'completed'
          executionStep.output = JSON.stringify(stepResult)
          executionStep.completedAt = new Date()
          executionStep.durationMs = executionStep.completedAt.getTime() - executionStep.startedAt.getTime()
          await stepRepo.save(executionStep)

          console.log(`[Workflow] Step ${workflowStep.name} completed in ${executionStep.durationMs}ms`)

          // Store step result in parameters for next steps
          parameters[`step_${index}_result`] = stepResult
        } catch (stepError) {
          console.error(`[Workflow] Step ${workflowStep.name} failed:`, stepError)

          executionStep.status = 'failed'
          executionStep.error = stepError.message
          executionStep.completedAt = new Date()
          executionStep.durationMs = executionStep.completedAt.getTime() - executionStep.startedAt.getTime()
          await stepRepo.save(executionStep)

          // Check if we should continue on error
          if (!workflowStep.continueOnError) {
            throw new Error(`Step ${workflowStep.name} failed: ${stepError.message}`)
          }

          console.log(`[Workflow] Continuing despite error (continueOnError=true)`)
        }
      }

      // All steps completed
      execution.status = 'completed'
      execution.summary = this.generateExecutionSummary(execution.steps)
      execution.completedAt = new Date()
      execution.totalDurationMs = Date.now() - startTime
      await executionRepo.save(execution)

      console.log(`[Workflow] Execution completed in ${execution.totalDurationMs}ms`)

      // Update workflow status
      const workflow = await workflowRepo.findOne({ where: { id: workflowId } })
      if (workflow) {
        workflow.lastExecutedAt = new Date()
        if (workflow.triggerType === TriggerType.Schedule) {
          // Calculate next execution time (simplified, needs proper cron parser)
          // workflow.nextExecutionAt = ...
        }
        await workflowRepo.save(workflow)
      }
    } catch (error) {
      console.error(`[Workflow] Execution failed:`, error)

      execution.status = 'failed'
      execution.error = error.message
      execution.summary = `Failed: ${error.message}`
      execution.completedAt = new Date()
      execution.totalDurationMs = Date.now() - startTime
      await executionRepo.save(execution)
    }
  }

  private async executeStep(
    stepType: WorkflowStepType,
    config: any,
    projectId: number,
    context: ResolverContext,
    parameters: any
  ): Promise<any> {
    switch (stepType) {
      case WorkflowStepType.ImportData:
        return await this.executeImportDataStep(config, projectId, context, parameters)

      case WorkflowStepType.GeneratePredictions:
        return await this.executeGeneratePredictionsStep(config, projectId, context, parameters)

      case WorkflowStepType.WaitForAnnotations:
        return await this.executeWaitForAnnotationsStep(config, projectId, context)

      case WorkflowStepType.SyncAnnotations:
        return await this.executeSyncAnnotationsStep(config, projectId, context, parameters)

      case WorkflowStepType.ValidateQuality:
        return await this.executeValidateQualityStep(config, projectId, context, parameters)

      case WorkflowStepType.Notification:
        return await this.executeNotificationStep(config, parameters)

      default:
        throw new Error(`Unknown step type: ${stepType}`)
    }
  }

  private async executeImportDataStep(
    config: any,
    projectId: number,
    context: ResolverContext,
    parameters: any
  ): Promise<any> {
    if (config.sourceType === 'dataset') {
      return await this.getDatasetIntegration().createLabelingTasksFromDataset(
        {
          projectId,
          dataSetId: config.dataSetId,
          imageField: config.imageField,
          autoGeneratePredictions: config.autoGeneratePredictions || false,
          limit: config.limit
        },
        context
      )
    } else {
      return await this.getExternalDataSource().importFromExternalSource(
        {
          projectId,
          source: {
            sourceType: config.sourceType,
            sourceUrl: config.sourceUrl,
            authHeader: config.authHeader,
            dataPath: config.dataPath
          },
          imageField: config.imageField,
          limit: config.limit
        },
        context
      )
    }
  }

  private async executeGeneratePredictionsStep(
    config: any,
    projectId: number,
    context: ResolverContext,
    parameters: any
  ): Promise<any> {
    const dataSetId = parameters.dataSetId || config.dataSetId

    if (!dataSetId) {
      throw new Error('dataSetId required for GeneratePredictions step')
    }

    return await this.getDatasetIntegration().generatePredictionsForDataset(
      {
        dataSetId,
        projectId,
        modelId: config.modelId,
        confidenceThreshold: config.confidenceThreshold,
        forceRegenerate: config.forceRegenerate || false
      },
      context
    )
  }

  private async executeWaitForAnnotationsStep(config: any, projectId: number, context: ResolverContext): Promise<any> {
    // This is a simplified implementation
    // In production, this should poll or use webhooks
    console.log(`[Workflow] WaitForAnnotations step - checking criteria`)

    // TODO: Implement actual waiting/polling logic
    return {
      status: 'waiting',
      message: 'WaitForAnnotations step - manual check required'
    }
  }

  private async executeSyncAnnotationsStep(
    config: any,
    projectId: number,
    context: ResolverContext,
    parameters: any
  ): Promise<any> {
    const dataSetId = parameters.dataSetId || config.dataSetId

    if (!dataSetId) {
      throw new Error('dataSetId required for SyncAnnotations step')
    }

    return await this.getDatasetIntegration().syncAnnotationsToDataset(
      {
        projectId,
        dataSetId,
        completedOnly: config.completedOnly !== false,
        sinceDate: config.sinceDate
      },
      context
    )
  }

  private async executeValidateQualityStep(
    config: any,
    projectId: number,
    context: ResolverContext,
    parameters: any
  ): Promise<any> {
    console.log(`[Workflow] ValidateQuality step`)

    // TODO: Implement quality validation logic
    return {
      status: 'validated',
      message: 'Quality validation - not yet implemented'
    }
  }

  private async executeNotificationStep(config: any, parameters: any): Promise<any> {
    console.log(`[Workflow] Notification step: ${config.message}`)

    // TODO: Implement actual notification (email, webhook, etc.)
    return {
      status: 'sent',
      message: config.message,
      recipients: config.recipients
    }
  }

  private evaluateCondition(condition: string, parameters: any): boolean {
    // Simple condition evaluation
    // In production, use a proper expression evaluator
    try {
      const conditionObj = JSON.parse(condition)
      // Example: { "step_0_result.tasksCreated": { "$gt": 0 } }
      // For now, always return true
      return true
    } catch (error) {
      console.warn(`[Workflow] Failed to evaluate condition: ${condition}`)
      return true
    }
  }

  private generateExecutionSummary(steps: WorkflowExecutionStepEntity[]): string {
    const completed = steps.filter(s => s.status === 'completed').length
    const failed = steps.filter(s => s.status === 'failed').length
    const skipped = steps.filter(s => s.status === 'skipped').length

    return `Completed ${completed}/${steps.length} steps (${failed} failed, ${skipped} skipped)`
  }
}
